// hooks/useEntitySchema.js
// Schema is loaded from SharePoint (SCMEntitySchema list) when authenticated.
// Falls back to localStorage when in demo/offline mode.
import { useState, useCallback, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { getAccessToken } from "../services/msalService";
import {
  fetchEntitySchema,
  createEntityInSchema,
  updateEntityInSchema,
  deleteEntityFromSchema,
  seedEntitySchema,
} from "../services/sharepointService";

// ─── Constants ───────────────────────────────────────────────────────────────

export const FIELD_TYPES = {
  number: {
    label: "Number",
    format: (v, u) =>
      v != null ? `${Number(v).toLocaleString()}${u ? " " + u : ""}` : "—",
  },
  currency: {
    label: "Currency",
    format: (v, u) => {
      if (v == null || v === "") return "—";
      const n = parseFloat(v);
      const sym = u || "$";
      if (isNaN(n)) return "—";
      if (n >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(2)}M`;
      if (n >= 1_000) return `${sym}${(n / 1_000).toFixed(1)}K`;
      return `${sym}${n.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
    },
  },
  percentage: {
    label: "Percentage (0–1)",
    format: (v) => (v != null ? `${(parseFloat(v) * 100).toFixed(0)}%` : "—"),
  },
  text: { label: "Text / Notes", format: (v) => v || "—" },
};

export const CHART_TYPES = ["bar", "line", "area"];

export const GRADIENT_OPTIONS = [
  {
    key: "blue",
    label: "Blue",
    css: "linear-gradient(135deg,#0052CC,#0041B8)",
  },
  {
    key: "green",
    label: "Green",
    css: "linear-gradient(135deg,#107C10,#0B5C0B)",
  },
  {
    key: "orange",
    label: "Orange",
    css: "linear-gradient(135deg,#D83B01,#A52801)",
  },
  {
    key: "purple",
    label: "Purple",
    css: "linear-gradient(135deg,#5C2D91,#40208C)",
  },
  {
    key: "teal",
    label: "Teal",
    css: "linear-gradient(135deg,#008272,#005F53)",
  },
  {
    key: "gold",
    label: "Gold",
    css: "linear-gradient(135deg,#986F0B,#7A5800)",
  },
];

export const DEFAULT_SCHEMA = [
  {
    id: "c1",
    name: "Contracts Executed",
    key: "contractsExecuted",
    type: "number",
    category: "Contracts & Procurement",
    icon: "📄",
    cardGradient: "blue",
    showOnDashboard: true,
    showInChart: true,
    chartType: "bar",
    unit: "",
    active: true,
  },
  {
    id: "c2",
    name: "Contracts Awarded",
    key: "contractsAwarded",
    type: "number",
    category: "Contracts & Procurement",
    icon: "🏆",
    cardGradient: "green",
    showOnDashboard: true,
    showInChart: true,
    chartType: "bar",
    unit: "",
    active: true,
  },
  {
    id: "c3",
    name: "Suppliers Onboarded",
    key: "suppliersOnboarded",
    type: "number",
    category: "Contracts & Procurement",
    icon: "🏢",
    cardGradient: "teal",
    showOnDashboard: true,
    showInChart: true,
    chartType: "bar",
    unit: "",
    active: true,
  },
  {
    id: "c4",
    name: "RFX Issued",
    key: "rfxIssued",
    type: "number",
    category: "Contracts & Procurement",
    icon: "📨",
    cardGradient: "purple",
    showOnDashboard: true,
    showInChart: true,
    chartType: "bar",
    unit: "",
    active: true,
  },
  {
    id: "c6",
    name: "Stakeholder Engagement",
    key: "stakeholderEngagement",
    type: "number",
    category: "Contracts & Procurement",
    icon: "🤝",
    cardGradient: "blue",
    showOnDashboard: true,
    showInChart: true,
    chartType: "bar",
    unit: "",
    active: true,
  },
  {
    id: "c7",
    name: "CSC Memos",
    key: "cscMemos",
    type: "number",
    category: "Contracts & Procurement",
    icon: "📝",
    cardGradient: "orange",
    showOnDashboard: true,
    showInChart: true,
    chartType: "bar",
    unit: "",
    active: true,
  },
  {
    id: "p1",
    name: "PO Count",
    key: "poCount",
    type: "number",
    category: "Purchase Orders",
    icon: "🛒",
    cardGradient: "blue",
    showOnDashboard: true,
    showInChart: true,
    chartType: "bar",
    unit: "",
    active: true,
  },
  {
    id: "p2",
    name: "PO Receipted",
    key: "poReceipted",
    type: "number",
    category: "Purchase Orders",
    icon: "✅",
    cardGradient: "green",
    showOnDashboard: true,
    showInChart: true,
    chartType: "bar",
    unit: "",
    active: true,
  },
  {
    id: "p3",
    name: "PO Avg Cycle Time",
    key: "poAvgCycleTime",
    type: "number",
    category: "Purchase Orders",
    icon: "⏱",
    cardGradient: "teal",
    showOnDashboard: true,
    showInChart: true,
    chartType: "line",
    unit: "days",
    active: true,
  },
  {
    id: "p4",
    name: "PO Spend",
    key: "poSpend",
    type: "currency",
    unit: "$",
    category: "Purchase Orders",
    icon: "💰",
    cardGradient: "gold",
    showOnDashboard: true,
    showInChart: true,
    chartType: "area",
    active: true,
  },
  {
    id: "p5",
    name: "Minor Tender Board Amount",
    key: "amountMinorTB",
    type: "currency",
    unit: "$",
    category: "Purchase Orders",
    icon: "📊",
    cardGradient: "purple",
    showOnDashboard: true,
    showInChart: true,
    chartType: "bar",
    active: true,
  },
  {
    id: "p6",
    name: "Major Tender Board Amount",
    key: "amountMajorTB",
    type: "currency",
    unit: "$",
    category: "Purchase Orders",
    icon: "🏛",
    cardGradient: "orange",
    showOnDashboard: true,
    showInChart: true,
    chartType: "bar",
    active: true,
  },
  {
    id: "cs1",
    name: "Total Cost Savings",
    key: "totalCostSavings",
    type: "currency",
    unit: "$",
    category: "Cost Savings",
    icon: "💰",
    cardGradient: "green",
    showOnDashboard: true,
    showInChart: true,
    chartType: "bar",
    active: true,
  },
  {
    id: "cs2",
    name: "Cost Savings Count",
    key: "costSavingsCount",
    type: "number",
    unit: "",
    category: "Cost Savings",
    icon: "🏷",
    cardGradient: "teal",
    showOnDashboard: true,
    showInChart: false,
    chartType: "bar",
    active: true,
  },
  {
    id: "d1",
    name: "Diesel Opening Stock",
    key: "dieselOpeningStock",
    type: "number",
    category: "Diesel Inventory",
    icon: "🛢",
    cardGradient: "gold",
    showOnDashboard: false,
    showInChart: true,
    chartType: "area",
    unit: "L",
    active: true,
  },
  {
    id: "d2",
    name: "Diesel Supply Received",
    key: "dieselSupply",
    type: "number",
    category: "Diesel Inventory",
    icon: "🔋",
    cardGradient: "green",
    showOnDashboard: false,
    showInChart: true,
    chartType: "bar",
    unit: "L",
    active: true,
  },
  {
    id: "d3",
    name: "Diesel Consumption",
    key: "dieselConsumption",
    type: "number",
    category: "Diesel Inventory",
    icon: "🔥",
    cardGradient: "orange",
    showOnDashboard: false,
    showInChart: true,
    chartType: "bar",
    unit: "L",
    active: true,
  },
  {
    id: "d4",
    name: "Diesel Closing Stock",
    key: "dieselClosingStock",
    type: "number",
    category: "Diesel Inventory",
    icon: "⛽",
    cardGradient: "teal",
    showOnDashboard: true,
    showInChart: true,
    chartType: "area",
    unit: "L",
    active: true,
  },
  {
    id: "l1",
    name: "Logistics Trips",
    key: "uyoLogisticsTrips",
    type: "number",
    category: "Logistics",
    icon: "🚚",
    cardGradient: "teal",
    showOnDashboard: true,
    showInChart: true,
    chartType: "bar",
    unit: "",
    active: true,
  },
  {
    id: "l2",
    name: "Logistics Activities Spend",
    key: "uyoLogisticsSpend",
    type: "currency",
    unit: "₦",
    category: "Logistics",
    icon: "💳",
    cardGradient: "blue",
    showOnDashboard: true,
    showInChart: true,
    chartType: "area",
    active: true,
  },
  {
    id: "pr1",
    name: "Portacabins Setup",
    key: "uyoPortacabins",
    type: "percentage",
    category: "Projects",
    icon: "🏗",
    cardGradient: "purple",
    showOnDashboard: true,
    showInChart: true,
    chartType: "line",
    unit: "",
    active: true,
  },
  {
    id: "pr2",
    name: "Logistics Base Progress",
    key: "uyoBaseProgress",
    type: "percentage",
    category: "Projects",
    icon: "🔧",
    cardGradient: "blue",
    showOnDashboard: true,
    showInChart: true,
    chartType: "line",
    unit: "",
    active: true,
  },
  {
    id: "pr3",
    name: "Catering OPLE-276 Progress",
    key: "cateringProgress",
    type: "percentage",
    category: "Projects",
    icon: "🍽",
    cardGradient: "orange",
    showOnDashboard: true,
    showInChart: true,
    chartType: "line",
    unit: "",
    active: true,
  },
  {
    id: "t1",
    name: "Process Improvement",
    key: "processImprovement",
    type: "text",
    category: "Notes",
    icon: "📋",
    cardGradient: "blue",
    showOnDashboard: false,
    showInChart: false,
    chartType: "bar",
    unit: "",
    active: true,
  },
  {
    id: "t2",
    name: "Highlights",
    key: "highlights",
    type: "text",
    category: "Notes",
    icon: "✨",
    cardGradient: "blue",
    showOnDashboard: false,
    showInChart: false,
    chartType: "bar",
    unit: "",
    active: true,
  },
];

// ─── localStorage fallback (demo / offline) ──────────────────────────────────
const LS_KEY      = "lekoil_scm_schema_v3";
const LS_KEY_PREV = "lekoil_scm_schema_v2";

function lsLoad() {
  try {
    // Read from current key, fall back to previous key
    let stored = null;
    for (const key of [LS_KEY, LS_KEY_PREV]) {
      const r = localStorage.getItem(key);
      if (r) { stored = JSON.parse(r); break; }
    }
    if (!stored || !Array.isArray(stored)) return DEFAULT_SCHEMA;

    const defaultMap = new Map(DEFAULT_SCHEMA.map((e) => [e.id, e]));
    const storedIds  = new Set(stored.map((e) => e.id));

    // Re-base each stored entry against its DEFAULT counterpart so every required
    // field exists (DEFAULT fills gaps; stored values override for user prefs).
    const rebased = stored.map((e) => {
      const def = defaultMap.get(e.id);
      return def ? { ...def, ...e } : e;
    });

    // Append any DEFAULT entries completely absent from stored (e.g. cs1/cs2)
    const missing = DEFAULT_SCHEMA.filter((e) => !storedIds.has(e.id));

    const result = [...rebased, ...missing];
    lsSave(result);
    return result;
  } catch {}
  return DEFAULT_SCHEMA;
}
function lsSave(schema) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(schema));
  } catch {}
}

// ─── Hook ────────────────────────────────────────────────────────────────────
export function useEntitySchema() {
  const { instance, accounts } = useMsal();
  const [schema, setSchema] = useState(lsLoad); // start from cache instantly
  const [loading, setLoading] = useState(false);
  const [schemaError, setSchemaError] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(true);
  const schemaMapRef = useRef({}); // key → full entity (including _spItemId)

  // Keep the ref map in sync — needed for updates/deletes by key
  useEffect(() => {
    schemaMapRef.current = Object.fromEntries(schema.map((e) => [e.id, e]));
  }, [schema]);

  // ── Load from SharePoint on mount ───────────────────────────────────────────
  useEffect(() => {
    if (!accounts?.length) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setSchemaError(null);
      try {
        const token = await getAccessToken(instance, accounts);
        const remoteSchema = await fetchEntitySchema(token);

        if (cancelled) return;

        if (remoteSchema.length === 0) {
          // List exists but is empty → seed it with defaults on first run
          console.info("[Schema] Remote list is empty — seeding defaults...");
          const seeded = await seedEntitySchema(token, DEFAULT_SCHEMA);
          const entities = seeded.filter((r) => r.success).map((r) => r.entity);
          setSchema(entities);
          lsSave(entities);
        } else {
          // Merge any DEFAULT_SCHEMA entries missing from SharePoint (e.g. cs1/cs2
          // added after the list was first seeded) — add them to SP so future loads
          // also include them.
          const remoteIds = new Set(remoteSchema.map((e) => e.id));
          const missing   = DEFAULT_SCHEMA.filter((e) => !remoteIds.has(e.id));
          if (missing.length) {
            console.info("[Schema] Adding missing entities to SharePoint:", missing.map(e => e.id));
            for (const entity of missing) {
              try { await createEntityInSchema(token, entity); } catch {}
            }
            const refreshed = await fetchEntitySchema(token);
            setSchema(refreshed);
            lsSave(refreshed);
          } else {
            setSchema(remoteSchema);
            lsSave(remoteSchema);
          }
        }
        setIsDemoMode(false);
      } catch (err) {
        if (!cancelled) {
          console.warn("[Schema] Falling back to localStorage:", err.message);
          setSchemaError(err.message);
          setIsDemoMode(true);
          // schema stays as localStorage value — no disruption to UX
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [instance, accounts]);

  // ─── Helper: get token (throws in demo mode — caller must handle) ──────────
  const getToken = useCallback(async () => {
    if (isDemoMode || !accounts?.length) return null;
    return getAccessToken(instance, accounts);
  }, [isDemoMode, instance, accounts]);

  // ─── Add ────────────────────────────────────────────────────────────────────
  const addEntity = useCallback(
    async (entity) => {
      const sortOrder = schema.length;
      const newEntity = {
        ...entity,
        id: `custom_${Date.now()}`,
        key: entity.key || entity.name.toLowerCase().replace(/[^a-z0-9]/g, "_"),
        active: true,
        sortOrder,
      };

      // Optimistic update
      setSchema((prev) => [...prev, newEntity]);
      lsSave([...schema, newEntity]);

      const token = await getToken();
      if (token) {
        try {
          const saved = await createEntityInSchema(token, newEntity);
          // Replace optimistic entry with real one (has _spItemId)
          setSchema((prev) =>
            prev.map((e) => (e.id === newEntity.id ? saved : e)),
          );
          lsSave(schema.map((e) => (e.id === newEntity.id ? saved : e)));
          return saved;
        } catch (err) {
          console.error(
            "[Schema] Failed to save entity to SharePoint:",
            err.message,
          );
        }
      }
      return newEntity;
    },
    [schema, getToken],
  );

  // ─── Update ─────────────────────────────────────────────────────────────────
  const updateEntity = useCallback(
    async (id, updates) => {
      const updated = schema.map((e) =>
        e.id === id ? { ...e, ...updates } : e,
      );
      setSchema(updated);
      lsSave(updated);

      const token = await getToken();
      if (token) {
        const entity = updated.find((e) => e.id === id);
        if (entity) {
          try {
            await updateEntityInSchema(token, entity);
          } catch (err) {
            console.error("[Schema] Failed to update entity:", err.message);
          }
        }
      }
    },
    [schema, getToken],
  );

  // ─── Remove ─────────────────────────────────────────────────────────────────
  const removeEntity = useCallback(
    async (id) => {
      const entity = schemaMapRef.current[id];
      setSchema((prev) => prev.filter((e) => e.id !== id));
      lsSave(schema.filter((e) => e.id !== id));

      const token = await getToken();
      if (token && entity) {
        try {
          await deleteEntityFromSchema(token, entity);
        } catch (err) {
          console.error("[Schema] Failed to delete entity:", err.message);
        }
      }
    },
    [schema, getToken],
  );

  // ─── Toggle active ───────────────────────────────────────────────────────────
  const toggleActive = useCallback(
    async (id) => {
      await updateEntity(id, { active: !schemaMapRef.current[id]?.active });
    },
    [updateEntity],
  );

  // ─── Reorder (updates sortOrder of all items) ────────────────────────────────
  const reorder = useCallback(
    async (fromIndex, toIndex) => {
      const next = [...schema];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      // Reassign sortOrder to match new positions
      const reordered = next.map((e, i) => ({ ...e, sortOrder: i }));
      setSchema(reordered);
      lsSave(reordered);

      const token = await getToken();
      if (token) {
        // Only update the two items that actually moved to minimise API calls
        const affected = [reordered[toIndex], reordered[fromIndex]].filter(
          Boolean,
        );
        for (const entity of affected) {
          try {
            await updateEntityInSchema(token, entity);
          } catch (err) {
            console.error("[Schema] Failed to reorder entity:", err.message);
          }
        }
      }
    },
    [schema, getToken],
  );

  // ─── Reset to defaults ───────────────────────────────────────────────────────
  const resetToDefault = useCallback(async () => {
    setSchema(DEFAULT_SCHEMA);
    lsSave(DEFAULT_SCHEMA);

    const token = await getToken();
    if (token) {
      // Delete all remote entities then re-seed
      for (const entity of schema) {
        if (entity._spItemId) {
          try {
            await deleteEntityFromSchema(token, entity);
          } catch {}
        }
      }
      await seedEntitySchema(token, DEFAULT_SCHEMA);
      // Reload from remote to get fresh _spItemIds
      try {
        const fresh = await fetchEntitySchema(token);
        setSchema(fresh);
        lsSave(fresh);
      } catch {}
    }
  }, [schema, getToken]);

  // ─── Derived state ───────────────────────────────────────────────────────────
  const activeEntities = schema.filter((e) => e.active);
  const dashboardEntities = activeEntities.filter(
    (e) => e.showOnDashboard && e.type !== "text",
  );
  const chartEntities = activeEntities.filter(
    (e) => e.showInChart && e.type !== "text",
  );
  const categories = [...new Set(activeEntities.map((e) => e.category))];
  const entitiesByCategory = categories.reduce((acc, cat) => {
    acc[cat] = activeEntities.filter((e) => e.category === cat);
    return acc;
  }, {});

  const formatValue = useCallback((entity, value) => {
    const fn = FIELD_TYPES[entity.type]?.format;
    return fn ? fn(value, entity.unit) : String(value ?? "—");
  }, []);

  return {
    schema,
    loading,
    schemaError,
    isDemoMode,
    activeEntities,
    dashboardEntities,
    chartEntities,
    categories,
    entitiesByCategory,
    addEntity,
    updateEntity,
    removeEntity,
    toggleActive,
    reorder,
    resetToDefault,
    formatValue,
  };
}
